import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { useAppSelector } from "../store";
import { TradeSummaryDto } from "./api/generated";

// Default input values
export const DEFAULT_INPUT = {
  code: "Select",
  logoURL: "",
  maxTotal: "0",
  price: 0,
  value: "0",
  units: "0",
  approx: "0",
};

export interface ITradeInput {
  code: string;
  logoURL: string;
  maxTotal: string;
  price: number;

  // 'value' is used to display the units in the sell/buy input field.
  // It is only for display purposes and represents the current input value.
  value: string;

  // 'units' is a behind-the-scenes counterpart to 'value'.
  // It is used to determine the known side of the trade when placing a trade summary.
  // 'units' also gets reset back to "0" when a summary is created, ensuring the correct state for the next trade.
  units: string;

  // 'approx' is another mirror of 'value', specifically used when creating a currency transaction (CT).
  // Since 'units' resets to "0" when a summary is created, 'approx' stores the original value of 'units' for reference.
  approx: string;
}

export interface ISwapDetails {
  total: number;
  isPromotional: boolean;
  partnershipCode: string | null;
  expectedOutput: {
    header: string;
    fee: number;
    value: string | number;
  };
}

export const DEFAULT_SWAP_DETAILS: ISwapDetails = {
  total: 0,
  isPromotional: false,
  partnershipCode: null,
  expectedOutput: {
    header: "",
    fee: 0,
    value: "0",
  },
};

export interface ITradeInput {
  code: string;
  logoURL: string;
  maxTotal: string;
  price: number;
  value: string;
  units: string;
  approx: string;
}

interface SwapSliceInterface {
  sellInput: ITradeInput;
  buyInput: ITradeInput;
  knownSide: "sell" | "buy";
  fetchRecentTrades: boolean;
  tradeDetails: ISwapDetails;
  tradeRequest: TradeSummaryDto | null;
}

const initialState: SwapSliceInterface = {
  sellInput: DEFAULT_INPUT,
  buyInput: DEFAULT_INPUT,
  knownSide: "sell",
  fetchRecentTrades: false,
  tradeDetails: DEFAULT_SWAP_DETAILS,
  tradeRequest: null,
};

export const SwapSlice = createSlice({
  name: "swap",
  initialState,
  reducers: {
    resetSellInput: (state) => {
      state.sellInput = DEFAULT_INPUT;
      state.knownSide = "sell";
      state.fetchRecentTrades = false;
    },
    resetBuyInput: (state) => {
      state.buyInput = DEFAULT_INPUT;
      state.knownSide = "sell";
      state.fetchRecentTrades = false;
    },
    setSellInput: (state, action: PayloadAction<Partial<ITradeInput>>) => {
      state.sellInput = { ...state.sellInput, ...action.payload };
    },
    setBuyInput: (state, action: PayloadAction<Partial<ITradeInput>>) => {
      state.buyInput = { ...state.buyInput, ...action.payload };
    },
    transerSellMaxTotal: (state) => {
      state.sellInput.value = state.sellInput.maxTotal;
      state.sellInput.units = state.sellInput.maxTotal;
    },
    swapInputs: (state) => {
      if (state.knownSide === "sell") {
        state.knownSide = "buy";
      } else if (state.knownSide === "buy") {
        state.knownSide = "sell";
      }
      const temp = { ...state.sellInput };

      state.sellInput = { ...state.buyInput };
      state.buyInput = temp;
    },
    setKnownSide: (state, action: PayloadAction<"sell" | "buy">) => {
      state.knownSide = action.payload;
    },
    setFetchRecentTrades: (state) => {
      state.fetchRecentTrades = true;
    },
    setTradeDetails: (state, action: PayloadAction<ISwapDetails>) => {
      state.tradeDetails = action.payload;
    },
    resetTradeDetails: (state) => {
      state.tradeDetails = DEFAULT_SWAP_DETAILS;
    },
    setTradeRequest: (state, action: PayloadAction<TradeSummaryDto>) => {
      state.tradeRequest = action.payload;
    },
    resetTradeRequest: (state) => {
      state.tradeRequest = null;
    },
  },
});

export const {
  resetSellInput,
  resetBuyInput,
  setSellInput,
  setBuyInput,
  setTradeDetails,
  resetTradeDetails,
  setTradeRequest,
  resetTradeRequest,
  transerSellMaxTotal,
  swapInputs,
  setKnownSide,
  setFetchRecentTrades,
} = SwapSlice.actions;

export const useSwapSlice = () => useAppSelector((state): SwapSliceInterface => state.swap);
